/**
 * Greenlight Processing Screen — The anticipation builder.
 * Shows animated progress as analysis runs, with teaser insights.
 */
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Logo1895 } from '@/components/Logo1895';

export default function GreenlightProcessing({ progress = 0, currentStep = '', analysis = {} }) {
  const teasers = [];

  if (analysis.scenes_parsed) {
    teasers.push(`Analyzing ${analysis.scenes_parsed} scenes...`);
  }
  if (analysis.writing_craft?.overall_score) {
    teasers.push(`Writing craft score: ${analysis.writing_craft.overall_score}/100`);
  }
  if (analysis.characters?.characters?.length) {
    teasers.push(`Found ${analysis.characters.characters.length} characters`);
  }
  if (analysis.characters?.protagonist) {
    teasers.push(`Protagonist identified: ${analysis.characters.protagonist}`);
  }
  if (analysis.emotional_architecture?.flow_metrics?.peak_count) {
    teasers.push(`${analysis.emotional_architecture.flow_metrics.peak_count} emotional peaks detected`);
  }
  if (analysis.narrative_logic?.issues?.length > 0) {
    teasers.push(`${analysis.narrative_logic.issues.length} logic issues found`);
  }
  if (analysis.character_arcs?.arcs?.length) {
    teasers.push(`${analysis.character_arcs.arcs.length} character arcs tracked`);
  }
  if (analysis.budget?.summary?.budget_tier) {
    teasers.push(`Budget tier: ${analysis.budget.summary.budget_tier}`);
  }
  if (analysis.budget?.summary?.estimated_shooting_days) {
    teasers.push(`~${analysis.budget.summary.estimated_shooting_days} shooting days estimated`);
  }
  if (analysis.risk?.summary?.critical_count > 0) {
    teasers.push(`${analysis.risk.summary.critical_count} critical risk scenes identified`);
  }
  if (analysis.dead_zones?.dead_zones?.length > 0) {
    teasers.push(`${analysis.dead_zones.dead_zones.length} dead zones detected`);
  }
  if (analysis.audience_segmentation?.primary_audience) {
    teasers.push(`Primary audience: ${analysis.audience_segmentation.primary_audience}`);
  }
  if (analysis.comparable_films?.comparable_films?.length) {
    teasers.push(`${analysis.comparable_films.comparable_films.length} comparable films identified`);
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-8">
      {/* Breathing logo */}
      <motion.div
        animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Logo1895 variant="mark" width={48} />
      </motion.div>

      {/* Progress ring */}
      <div className="relative w-24 h-24">
        <svg width={96} height={96} className="-rotate-90">
          <circle cx={48} cy={48} r={38} fill="none" stroke="var(--border-subtle)" strokeWidth={4} />
          <motion.circle
            cx={48} cy={48} r={38} fill="none"
            stroke="#FFD700" strokeWidth={4} strokeLinecap="round"
            strokeDasharray={238.76}
            animate={{ strokeDashoffset: 238.76 - (progress / 100) * 238.76 }}
            transition={{ duration: 0.5 }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-[var(--text-primary)]">
          {progress}%
        </span>
      </div>

      {/* Current step */}
      <div className="space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin text-amber-400" />
          <span className="text-sm text-[var(--text-primary)]">{currentStep}</span>
        </div>

        {/* Teaser insights */}
        <div className="space-y-1">
          {teasers.map((teaser, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.3 }}
              className="text-xs text-[var(--text-muted)]"
            >
              {teaser}
            </motion.p>
          ))}
        </div>
      </div>
    </div>
  );
}
